// Hideo - script editor's writing surface: the block handle (#blockHandle, mouse only: "+" adds below, the grip ⠿
// drags or opens the menu), dragging a block (#blockDrop shows where it goes; Alt = copy), the block menu (#blockMenu:
// turn into, move, duplicate, delete) and Ctrl+D. Ported from the legacy editor/editor.js with the same HTML, except that
// the menu's buttons carry their action (data-act, data-arg) instead of inline handlers: installBlocks adds the menu's
// and the "+" button's listeners.
// A duplicated block (Ctrl+D, the menu's Duplicate, Alt + drag) keeps no comment highlight: the comment stays on the
// original text (before, the copy had a second highlight with the same comment id).
// A heading moves with its section (blockSection: the blocks under it, up to the next heading of the same or a higher
// level), by drag and with the menu's move up / down, which jump over a whole section: moved alone, a heading left its
// text behind, glued to the text above (user report, 24/10/2026). Copies never take the section: Duplicate, Ctrl+D and
// Alt + drag copy only the block itself (user request, 30/10/2026).
import { t } from '../../../i18n'
import { useTocStore } from '../../../stores/toc'
import { applyBlock } from './commands'
import { byId, edBlockOf, edBody, edCaret, emptyBlock, listKind, queueScriptSave, setSavedRange } from './core'
import { slashCheck } from './slash'
import { blockIcon, blockName } from '../blockKinds'

let hoverBlk: HTMLElement | null = null, dragBlk: HTMLElement | null = null, menuBlk: HTMLElement | null = null
let dragInfo: { x: number; y: number; moved: boolean; copy: boolean } | null = null, dragTarget: Element | null = null
const queueToc = () => useTocStore().queue()

const headLevel = (el: Element | null): number => (el && /^H[1-3]$/.test(el.tagName) ? +el.tagName[1] : 0)
/** The blocks that move with a block: a heading (h1–h3) and the blocks under it up to the next heading of the same or a
 *  higher level; any other block alone */
function blockSection(b: HTMLElement): HTMLElement[] {
  const L = headLevel(b), out = [b]
  if (!L) return out
  for (let n = b.nextElementSibling as HTMLElement | null; n; n = n.nextElementSibling as HTMLElement | null) {
    const l = headLevel(n)
    if (l && l <= L) break
    out.push(n)
  }
  return out
}
/** A copy of a block without the comment highlights (the text stays) */
function blockCopy(b: HTMLElement): HTMLElement {
  const c = b.cloneNode(true) as HTMLElement
  c.querySelectorAll('span.cmt[data-cmt]').forEach(sp => sp.replaceWith(...sp.childNodes))
  return c
}
function blockAtY(y: number): Element | null { return [...edBody().children].find(c => { const r = c.getBoundingClientRect(); return y >= r.top - 6 && y <= r.bottom + 6 }) || null }
function showBlockHandle(b: HTMLElement): void {
  hoverBlk = b
  const h = byId('blockHandle'), wr = byId('scriptPageWrap').getBoundingClientRect(), br = b.getBoundingClientRect()
  const lh = parseFloat(getComputedStyle(b).lineHeight), line = Math.min(br.height, isNaN(lh) ? 28 : lh)
  h.style.top = (br.top - wr.top + Math.max(0, (line - 26) / 2)) + 'px'
  h.style.left = Math.max(2, br.left - wr.left - 50) + 'px'
  h.classList.remove('hidden')
}
export function hideBlockHandle(): void { if (!dragBlk && byId('blockMenu').classList.contains('hidden')) { byId('blockHandle').classList.add('hidden'); hoverBlk = null } }
function blockDropAt(y: number): void {
  const kids = [...edBody().children], wr = byId('scriptPageWrap').getBoundingClientRect(), line = byId('blockDrop')
  dragTarget = kids.find(c => { const r = c.getBoundingClientRect(); return y < r.top + r.height / 2 }) || null
  const ref = dragTarget || kids[kids.length - 1]
  if (!ref) return
  const r = ref.getBoundingClientRect()
  line.style.top = ((dragTarget ? r.top - 4 : r.bottom + 2) - wr.top) + 'px'
  line.style.left = (r.left - wr.left) + 'px'; line.style.width = r.width + 'px'
  line.classList.remove('hidden')
}
// Alt while dragging = COPY: the block stays where it is and a copy goes to the drop point.
// Alt can be pressed or released mid-drag: what counts is its state on drop.
function setDragCopy(on: boolean): void {
  if (!dragBlk || !dragInfo || !dragInfo.moved) return
  dragInfo.copy = on; blockSection(dragBlk).forEach(x => x.classList.toggle('block-dragging', !on))
  byId('blockDrop').classList.toggle('drop-copy', on); document.body.classList.toggle('drag-copy', on)
}
function endBlockDrag(drop: boolean): void {
  const b = dragBlk, info = dragInfo
  dragBlk = null; dragInfo = null
  byId('blockDrop').classList.add('hidden'); byId('blockDrop').classList.remove('drop-copy'); document.body.classList.remove('drag-copy')
  if (!b || !info) return
  blockSection(b).forEach(x => { x.classList.remove('block-dragging'); if (!x.className) x.removeAttribute('class') })
  if (!info.moved) { if (drop) openBlockMenu(b); return }
  let moved = b
  const body = edBody(), sec = blockSection(b), put = (els: HTMLElement[]) => els.forEach(x => { if (dragTarget) body.insertBefore(x, dragTarget); else body.appendChild(x) })
  if (drop && info.copy) { moved = blockCopy(b); put([moved]); queueScriptSave(); queueToc() }
  else if (drop && !sec.includes(dragTarget as HTMLElement)) { put(sec); queueScriptSave(); queueToc() }
  showBlockHandle(moved)
}
/** Ctrl+D (Cmd+D) = duplicate the block with the cursor */
export function blockDupAtCaret(): boolean {
  const s = getSelection() as Selection, b = s.rangeCount ? edBlockOf(s.anchorNode) : null
  if (!b) return false
  b.after(blockCopy(b)); queueScriptSave(); queueToc(); return true
}
let BLOCK_TURN: [string, string, string][] = []
function openBlockMenu(b: HTMLElement): void {
  menuBlk = b
  const m = byId('blockMenu')
  const it = (act: string, icon: string, label: string) => `<button type="button" ${act} class="w-full flex items-center gap-3 px-2.5 py-1.5 rounded-lg text-sm text-gray-200 hover:bg-darkbtn hover:text-brand text-left"><i class="fas ${icon} w-5 text-center text-gray-400"></i>${label}</button>`
  m.innerHTML = '<div class="px-2.5 pt-1 pb-1 text-[10px] font-bold uppercase tracking-wider text-gray-500">' + t('Turn into') + '</div>'
    + BLOCK_TURN.map(([l, i, v]) => it(`data-act="turn" data-arg="${v}"`, i, l)).join('')
    + '<div class="h-px bg-gray-800 my-1"></div>'
    + it('data-act="move" data-arg="-1"', 'fa-arrow-up', t('Move up')) + it('data-act="move" data-arg="1"', 'fa-arrow-down', t('Move down')) + it('data-act="dup"', 'fa-clone', t('Duplicate')) + it('data-act="del"', 'fa-trash', t('Delete'))
  m.classList.remove('hidden')
  const r = byId('blockGrip').getBoundingClientRect(), h = m.offsetHeight
  m.style.left = Math.max(8, Math.min(r.left, innerWidth - m.offsetWidth - 8)) + 'px'
  m.style.top = (r.bottom + h + 8 > innerHeight ? Math.max(8, r.top - h - 4) : r.bottom + 4) + 'px'
}
export function hideBlockMenu(): void { byId('blockMenu').classList.add('hidden'); menuBlk = null }
export const blockMenuShown = () => !byId('blockMenu').classList.contains('hidden')
/** "Turn into": the block becomes another type. A list first stops being a list (a checklist too), then gets the new
 *  type; from a list to another kind of list it is converted directly */
export function blockTurn(v: string): void {
  const b = menuBlk
  hideBlockMenu()
  if (!b || !b.isConnected) return
  edBody().focus()
  const r = document.createRange(); r.selectNodeContents(b)
  const s = getSelection() as Selection; s.removeAllRanges(); s.addRange(r); setSavedRange(r.cloneRange())
  if (/^(UL|OL)$/.test(b.tagName)) {
    const cur = listKind(b)
    if (v === cur) return
    if (v === 'ul' || v === 'ol' || v === 'blockquote') return applyBlock(v)   // to another kind of list, or a quote of all its items
    applyBlock(cur)   // remove the list, then apply the new style
    const s2 = getSelection() as Selection
    if (s2.rangeCount) setSavedRange(s2.getRangeAt(0).cloneRange())
    if (v === 'p') return
  }
  applyBlock(v); queueToc()
}
export function blockMove(d: number): void {
  const b = menuBlk
  hideBlockMenu()
  if (!b) return
  // A heading moves with its section and jumps over the whole section of the same level above or below (with its
  // sub-sections); past its parent heading it goes one step (just above it, or among the parent's first blocks).
  // Other blocks, and text outside the sections, move one block at a time
  const sec = blockSection(b), L = headLevel(b), end = sec[sec.length - 1]
  if (d < 0) {
    const prev = b.previousElementSibling
    if (!prev) return
    let to: Element = prev
    if (L) for (let n: Element | null = prev; n; n = n.previousElementSibling) { const l = headLevel(n); if (l === L) { to = n; break } if (l && l < L) break }
    to.before(...sec)
  } else {
    const next = end.nextElementSibling as HTMLElement | null
    if (!next) return
    let last: Element = next
    if (L && headLevel(next) === L) last = blockSection(next).slice(-1)[0]
    else if (L) while (last.nextElementSibling && !headLevel(last.nextElementSibling)) last = last.nextElementSibling
    last.after(...sec)
  }
  b.scrollIntoView({ block: 'nearest' }); showBlockHandle(b); queueScriptSave(); queueToc()
}
export function blockDup(): void { const b = menuBlk; hideBlockMenu(); if (!b) return; b.after(blockCopy(b)); queueScriptSave(); queueToc() }
export function blockDel(): void { const b = menuBlk; hideBlockMenu(); if (!b) return; b.remove(); hideBlockHandle(); queueScriptSave(); queueToc() }
/** "+": a new paragraph below the block, with the "/" menu open */
export function blockAddBelow(): void {
  const b = hoverBlk, p = emptyBlock()
  if (b && b.isConnected) b.after(p); else edBody().appendChild(p)
  edBody().focus(); edCaret(p, 0); document.execCommand('insertText', false, '/'); slashCheck()
}
/** Forgets a drag in progress (edHideFloating) */
export function dropDrag(): void { dragBlk = null }

/** Mousedown on a button keeps the page's selection (preventDefault); a click runs it (they replace the inline handlers
 *  of the "+" button and of the menu's buttons, with the same effects) */
function installButtons(): void {
  const add = byId('blockHandle').querySelector('button:not(#blockGrip)') as HTMLElement, m = byId('blockMenu')
  add.addEventListener('mousedown', e => e.preventDefault())
  add.addEventListener('click', () => blockAddBelow())
  m.addEventListener('mousedown', e => { const b = (e.target as Element).closest('button'); if (b && m.contains(b)) e.preventDefault() })
  m.addEventListener('click', e => {
    const b = (e.target as Element).closest<HTMLElement>('button[data-act]')
    if (!b || !m.contains(b)) return
    const act = b.dataset.act, arg = b.dataset.arg as string
    if (act === 'turn') blockTurn(arg)
    else if (act === 'move') blockMove(Number(arg))
    else if (act === 'dup') blockDup()
    else if (act === 'del') blockDel()
  })
}
/** The handle's listeners (in the legacy order): hovering the page (fine pointer only), the grip's pointer events, and
 *  Alt pressed or released mid-drag (window, capture phase; preventDefault stops Alt opening the browser menu on
 *  Windows); then the buttons' own listeners */
export function installBlocks(body: HTMLElement, win: Pick<Window, 'addEventListener'>): void {
  BLOCK_TURN = (['p', 'h1', 'h2', 'h3', 'ul', 'ol', 'blockquote', 'pre'] as const).map(k => [blockName(k), blockIcon(k), k])
  if (matchMedia('(pointer: fine)').matches) {
    body.addEventListener('mousemove', e => {
      if (dragBlk) return
      const b = edBlockOf(e.target as Node) || blockAtY(e.clientY) as HTMLElement | null
      if (b && b !== hoverBlk) showBlockHandle(b)
    })
    byId('scriptPageWrap').addEventListener('mouseleave', hideBlockHandle)
  }
  const grip = byId('blockGrip')
  grip.addEventListener('pointerdown', e => {
    if (!hoverBlk || e.button !== 0) return
    e.preventDefault()
    dragBlk = hoverBlk; dragInfo = { x: e.clientX, y: e.clientY, moved: false, copy: false }; grip.setPointerCapture(e.pointerId)
  })
  grip.addEventListener('pointermove', e => {
    if (!dragBlk || !dragInfo) return
    if (!dragInfo.moved) {
      if (Math.hypot(e.clientX - dragInfo.x, e.clientY - dragInfo.y) < 4) return
      dragInfo.moved = true; blockSection(dragBlk).forEach(x => x.classList.add('block-dragging')); hideBlockMenu()
    }
    if (e.altKey !== dragInfo.copy) setDragCopy(e.altKey)
    const wrap = byId('scriptVisualWrap'), w = wrap.getBoundingClientRect()
    if (e.clientY < w.top + 40) wrap.scrollTop -= 14; else if (e.clientY > w.bottom - 40) wrap.scrollTop += 14
    blockDropAt(e.clientY)
  })
  grip.addEventListener('pointerup', e => { if (dragInfo && dragInfo.moved) dragInfo.copy = e.altKey; endBlockDrag(true) })
  grip.addEventListener('pointercancel', () => endBlockDrag(false))
  ;(['keydown', 'keyup'] as const).forEach(type => win.addEventListener(type, e => { if ((e as KeyboardEvent).key === 'Alt' && dragBlk) { e.preventDefault(); setDragCopy(type === 'keydown') } }, true))
  installButtons()
}
